import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { DETAIL_TAB_PARAM } from "./detailRoutes";

/** `"Cloud Propagation"` → `"cloud-propagation"`: what the sub-tab looks like in a URL. */
export function toTabSlug(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** The tab whose slug is `raw`, or `fallback` for an absent or unknown one. */
export function resolveTabParam<T extends string>(
  raw: string | null,
  tabs: readonly T[],
  fallback: T
): T {
  if (!raw) return fallback;
  return tabs.find((tab) => toTabSlug(tab) === raw) ?? fallback;
}

/**
 * A detail view's active sub-tab, kept in `?tab=` so a reload or a pasted link lands on
 * the same one (CTX-773). The pattern `BackboneDetailsTabs` already followed.
 *
 * Switching replaces the history entry instead of pushing one: Back leaves the detail,
 * it does not walk through every sub-tab visited on the way. The default tab is written
 * as no param at all, so the plain detail URL stays what it was.
 */
export function useDetailTabParam<T extends string>(
  tabs: readonly T[],
  fallback: T
): [T, (next: string) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = resolveTabParam(searchParams.get(DETAIL_TAB_PARAM), tabs, fallback);

  const setActiveTab = useCallback(
    (next: string) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next === fallback) params.delete(DETAIL_TAB_PARAM);
          else params.set(DETAIL_TAB_PARAM, toTabSlug(next));
          return params;
        },
        { replace: true }
      );
    },
    [fallback, setSearchParams]
  );

  return [activeTab, setActiveTab];
}
